import axios from 'axios';
import { apiConfig } from '@/config';
import { attachAuthInterceptors, getOidcTokenFromStorage } from './authInterceptors';

/**
 * Axios API client with authentication and tenant interceptors
 */
export const apiClient = axios.create({
  baseURL: apiConfig.baseUrl,
  timeout: apiConfig.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Token getter – defaults to reading the OIDC localStorage store so API calls
 * that fire before AppShell mounts still carry a Bearer token. AppShell swaps
 * in the renew-or-fail getter (AuthProvider.getAccessToken) via setTokenGetter.
 */
let getAccessToken: () => Promise<string | null> = async () => getOidcTokenFromStorage();

/**
 * Set the token getter function (called by AppShell with AuthProvider's getter)
 */
export const setTokenGetter = (getter: () => Promise<string | null>) => {
  getAccessToken = getter;
};

// Shared request/response interceptors (auth + tenant + ND 401 refresh +
// session-expired routing). Consolidated in authInterceptors.ts so both this
// orval mutator and the swagger-typescript-api `Api` instance stay in sync.
attachAuthInterceptors(apiClient, { getToken: () => getAccessToken() });

/**
 * ABP API error response structure
 */
export interface AbpErrorResponse {
  error: {
    code?: string;
    message: string;
    details?: string;
    validationErrors?: Array<{
      message: string;
      members?: string[];
    }>;
  };
}

/**
 * Extract error message from ABP error response
 */
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const abpError = error.response?.data as AbpErrorResponse | undefined;
    if (abpError?.error?.message) {
      return abpError.error.message;
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Orval custom axios instance for generated API clients
 * This is used by orval's mutator configuration
 */
export const customInstance = <T>(
  config: {
    url: string;
    method: 'get' | 'post' | 'put' | 'delete' | 'patch';
    params?: Record<string, unknown>;
    data?: unknown;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  },
): Promise<T> => {
  const source = axios.CancelToken.source();

  const promise = apiClient<T>({
    url: config.url,
    method: config.method,
    params: config.params,
    data: config.data,
    headers: config.headers,
    cancelToken: source.token,
  }).then(({ data }) => data);

  // @ts-expect-error - Adding cancel method to promise for TanStack Query
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  // Support for AbortSignal
  if (config.signal) {
    config.signal.addEventListener('abort', () => {
      source.cancel('Query was cancelled');
    });
  }

  return promise;
};

export default customInstance;
